import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faSpinner } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import MarkdownEditor from '@/components/ui/MarkdownEditor.tsx';
import ThumbnailUpload from '@/components/ui/ThumbnailUpload.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import type { Video, ModuleID } from '@/models';
import type { CreateVideoRequest } from '@/modelsAPI';

interface CreateVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleID;
  onVideoCreated: (video: Video) => void;
  moduleThumbnail?: string;
}

export default function CreateVideoModal({ 
  isOpen, 
  onClose, 
  moduleId,
  onVideoCreated,
  moduleThumbnail 
}: CreateVideoModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    order: 0
  });
  const [thumbnailData, setThumbnailData] = useState<{ file: File; base64: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // Load module thumbnail and get max order when modal opens
  useEffect(() => {
    if (isOpen) {
      // Load module thumbnail as default
      if (moduleThumbnail && !thumbnailData) {
        // Fetch the module thumbnail and convert to base64
        fetch(APIRoutes.getBlobContentUrl(moduleThumbnail))
          .then(response => response.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result as string;
              // Create a fake File object for consistency with the ThumbnailUpload component
              const file = new File([blob], 'module-thumbnail.jpg', { type: blob.type });
              setThumbnailData({ file, base64 });
            };
            reader.readAsDataURL(blob);
          })
          .catch(err => {
            console.error('Error loading module thumbnail:', err);
          });
      }

      // Get the highest order value
      APIRoutes.listVideos(moduleId, {
        limit: 1,
        sort: 'order',
        sortDir: 'DESC'
      })
        .then(response => {
          const maxOrder = response.videos.length > 0 ? response.videos[0].order : -1;
          setFormData(prev => ({ ...prev, order: maxOrder + 1 }));
        })
        .catch(err => {
          console.error('Error fetching max order:', err);
          // Default to 0 if fetching fails
          setFormData(prev => ({ ...prev, order: 0 }));
        });
    }
  }, [isOpen, moduleThumbnail, moduleId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      showErrorToast('Bitte geben Sie einen Titel ein');
      return;
    }


    if (!thumbnailData?.base64) {
      showErrorToast('Bitte wählen Sie ein Thumbnail aus');
      return;
    }

    try {
      setLoading(true);
      
      const requestData: CreateVideoRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        order: formData.order,
        thumbnailBase64: thumbnailData.base64
      };
      
      const response = await APIRoutes.createVideo(moduleId, requestData);
      
      onVideoCreated(response);
      onClose();
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        order: 0
      });
      setThumbnailData(null);
    } catch (err) {
      console.error('Error creating video:', err);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form when closing
      setFormData({
        title: '',
        description: '',
        order: 0
      });
      setThumbnailData(null);
      onClose();
    }
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Neues Video erstellen"
      icon={<FontAwesomeIcon icon={faVideo} className="w-5 h-5 text-blue-600" />}
      closeDisabled={loading}
      maxWidth="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Titel *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="z.B. Einführung in die Candlesticks"
              disabled={loading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              maxLength={200}
              required
            />
          </div>

          <div>
            <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reihenfolge
            </label>
            <input
              type="number"
              id="order"
              value={formData.order}
              onChange={(e) => setFormData((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
              disabled={loading}
              min={0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung
          </label>
          <MarkdownEditor
            value={formData.description}
            onChange={(value) => setFormData((prev) => ({ ...prev, description: value ?? '' }))}
            placeholder="Eine detaillierte Beschreibung des Videos... (Markdown unterstützt)"
            disabled={loading}
            height={200}
          />
        </div>

        <ThumbnailUpload
          onThumbnailChange={setThumbnailData}
          disabled={loading}
          required
          initialThumbnail={thumbnailData}
        />

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Erstelle...
              </>
            ) : (
              'Video erstellen'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}